import React, { Component, Fragment } from "react";
import { inject, observer } from "mobx-react";
import { withRouter } from "react-router-dom";
import CircularProgress from "@material-ui/core/CircularProgress";

import {
    Grid,
    Typography,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableFooter,
    IconButton,
    Paper,
    Divider,
    TextField,
    InputAdornment
} from "@material-ui/core";

import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import WarningIcon from "@material-ui/icons/Warning";
import FirstPageIcon from "@material-ui/icons/FirstPage";
import LastPageIcon from "@material-ui/icons/LastPage";
import KeyboardArrowLeftIcon from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";

import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import MainContainer from "../../components/MainContainer";
import Heading from "../../components/Heading";
import HGSuperPayEmployeeModal from "../../components/superPay/HGSuperPayEmployeeModal";
import MvrStatus, { getMvrDetails } from "../../components/superPay/MvrStatus";
import MvrIntroductionDialog from "../../components/superPay/MvrIntroductionDialog";
import { getEmployeeAction, canVerifyMvr } from "../../components/superPay/employeeAction";
import { resolveVisibleMvrAnnouncement } from "../../helper/mvrAnnouncement";

const HOT_PINK = "#ff0c9c";

function formatDob(emp) {
    if (!emp) return "";
    if (emp.dobDay && emp.dobMonth && emp.dobYear) {
        return `${String(emp.dobDay).padStart(2, "0")}/${String(emp.dobMonth).padStart(2, "0")}/${emp.dobYear}`;
    }
    return emp.dob || "";
}

function formatAddress(emp) {
    if (!emp) return "";
    const parts = [
        emp.addressLine1, emp.addressLine2, emp.addressLine3, emp.addressLine4,
        emp.suburb, emp.state, emp.postcode
    ].filter(Boolean);
    return parts.join(", ");
}

function parseApiError(e, fallback) {
    const raw = e?.response?.data;
    const data = raw?.response || raw;
    if (typeof data?.errors === "string" && data.errors) {
        return data.errors;
    }
    if (data?.errors && typeof data.errors === "object" && !Array.isArray(data.errors)) {
        const msgs = Object.entries(data.errors).flatMap(([field, errs]) =>
            Array.isArray(errs) ? errs.map(m => `${field}: ${m}`) : [`${field}: ${errs}`]
        );
        if (msgs.length) return msgs.join(", ");
    }
    return data?.message || data?.error || e?.message || fallback;
}

function buildUpdateFields(emp) {
    if (!emp) return {};
    const fields = {};
    const keys = [
        "email", "firstName", "lastName",
        "dobDay", "dobMonth", "dobYear",
        "addressLine1", "addressLine2", "addressLine3", "addressLine4",
        "suburb", "postcode", "state",
        "taxFileNumber", "usi", "memberNumber", "employmentPayrollNumber", "abn"
    ];
    for (const k of keys) {
        if (emp[k] !== undefined && emp[k] !== null) fields[k] = emp[k];
    }
    return fields;
}

@inject("profileStore", "templateStore", "hgSuperPayStore", "organisationStore")
@withRouter
@observer
class HGSuperPayEmployees extends Component {
    state = {
        employees: [],
        rowsEmployerProfileId: null,
        totalCount: 0,
        loading: false,
        mvrIntroductionVisible: null,
        mvrIntroductionContext: "",
        verifyingContactId: null,
        modalOpen: false,
        selectedContact: null,
        mvrPreview: null,
        deleteConfirmOpen: false,
        deleteContact: null,
        page: 1,
        rowsPerPage: 20,
        pagination: null,
        search: "",
        mappedFilter: null   // null = All, true = Mapped, false = Not Mapped
    };

    componentDidMount() {
        this._mounted = true;
        this._effectiveEmployerProfileId = this.getEmployerProfileId();
        this._effectiveActorKey = this.getActorKey();
        this._loadMapping();
    }

    componentDidUpdate() {
        const employerProfileId = this.getEmployerProfileId();
        const actorKey = this.getActorKey();
        if (employerProfileId !== this._effectiveEmployerProfileId || actorKey !== this._effectiveActorKey) {
            this._effectiveEmployerProfileId = employerProfileId;
            this._effectiveActorKey = actorKey;
            this._mappingRequestId = (this._mappingRequestId || 0) + 1;
            clearTimeout(this._searchTimer);
            this._routeLookupStartedFor = null;
            this._autoOpenedRouteContactId = null;
            this.setState({
                employees: [],
                rowsEmployerProfileId: null,
                totalCount: 0,
                pagination: null,
                page: 1,
                search: "",
                mappedFilter: null,
                modalOpen: false,
                selectedContact: null,
                mvrPreview: null
            }, () => this._loadMapping(1, "", null));
        }
    }

    componentWillUnmount() {
        this._mounted = false;
        clearTimeout(this._searchTimer);
        this._routeLookupStartedFor = null;
    }

    getEmployerProfileId = () => {
        const { profile } = this.props.profileStore;
        return this.props.profileId || (profile && profile.id);
    };

    getActorKey = () => {
        const actor = this.props.profileStore.currentProfile || this.props.profileStore.profile;
        return `${actor?.userId || ""}:${actor?.id || ""}`;
    };

    _loadMapping = (requestedPage = this.state.page, requestedSearch = this.state.search, requestedMapped = this.state.mappedFilter, silent = false, suppressError = false) => {
        const profileId = this.getEmployerProfileId();
        if (!profileId) return;
        const requestId = (this._mappingRequestId || 0) + 1;
        this._mappingRequestId = requestId;
        if (!silent) this.setState({ loading: true });
        return this.props.hgSuperPayStore.getEmployeeMapping(profileId, requestedPage, this.state.rowsPerPage, requestedSearch, requestedMapped)
            .then(({ items, total, pagination }) => {
                if (requestId !== this._mappingRequestId || profileId !== this.getEmployerProfileId()) return;
                const nextPage = pagination && pagination.page ? pagination.page : requestedPage;
                this.setState({
                    employees: items,
                    rowsEmployerProfileId: profileId,
                    totalCount: total,
                    pagination,
                    page: nextPage
                });
                this._maybeAutoOpenFromRoute(items, pagination);
            })
            .catch(() => {
                if (!suppressError && requestId === this._mappingRequestId && profileId === this.getEmployerProfileId()) {
                    this.props.templateStore.openSnackbar("Failed to load employee mapping");
                }
            })
            .finally(() => {
                if (requestId === this._mappingRequestId && profileId === this.getEmployerProfileId()) {
                    this.setState({ loading: false });
                }
            });
    };

    handlePageChange = page => {
        const { pagination, loading } = this.state;
        if (loading || page < 1 || (pagination && page > pagination.totalPages)) return;
        this._loadMapping(page);
    };

    handleSearchChange = event => {
        const search = event.target.value;
        clearTimeout(this._searchTimer);
        this._mappingRequestId = (this._mappingRequestId || 0) + 1;
        this.setState({ search, page: 1, loading: true }, () => {
            this._searchTimer = setTimeout(() => this._loadMapping(1, search, this.state.mappedFilter), 300);
        });
    };

    handleClearSearch = () => {
        clearTimeout(this._searchTimer);
        this._mappingRequestId = (this._mappingRequestId || 0) + 1;
        this.setState({ search: "", page: 1, loading: true }, () => {
            this._loadMapping(1, "", this.state.mappedFilter);
        });
    };

    handleMappedFilterChange = (value) => {
        // value: null (All), true (Mapped), false (Not Mapped)
        clearTimeout(this._searchTimer);
        this._mappingRequestId = (this._mappingRequestId || 0) + 1;
        this.setState({ mappedFilter: value, page: 1, loading: true }, () => {
            this._loadMapping(1, this.state.search, value);
        });
    };

    _maybeAutoOpenFromRoute = (items, pagination) => {
        const match = this.props.match;
        const routedContactId = match && match.params && match.params.profileId;
        if (!routedContactId || this._autoOpenedRouteContactId === String(routedContactId)) return;
        const contact = (items || []).find(c => String(c.contactId) === String(routedContactId));
        if (contact && contact.isMapped) {
            this._autoOpenedRouteContactId = String(routedContactId);
            this.handleOpenUpdateModal(contact);
            return;
        }

        if (this._routeLookupStartedFor === String(routedContactId) || !pagination || !pagination.hasNext) return;
        this._routeLookupStartedFor = String(routedContactId);
        this._findRouteContactOnOtherPages(routedContactId, pagination);
    };

    _findRouteContactOnOtherPages = async (contactId, pagination) => {
        const { profile } = this.props.profileStore;
        const employerProfileId = this.props.profileId || (profile && profile.id);
        if (!employerProfileId) return;

        for (let nextPage = pagination.page + 1; nextPage <= pagination.totalPages; nextPage += 1) {
            try {
                const { items } = await this.props.hgSuperPayStore.getEmployeeMapping(
                    employerProfileId,
                    nextPage,
                    pagination.limit
                );
                if (this._routeLookupStartedFor !== String(contactId)) return;
                const contact = (items || []).find(item => String(item.contactId) === String(contactId));
                if (contact && contact.isMapped) {
                    this._autoOpenedRouteContactId = String(contactId);
                    this.handleOpenUpdateModal(contact);
                    return;
                }
            } catch (error) {
                return;
            }
        }
    };

    handleOpenCreateModal = (contact) => {
        this.setState({ modalOpen: true, selectedContact: contact, mvrPreview: null });
    };

    handleOpenUpdateModal = (contact) => {
        this.setState({ modalOpen: true, selectedContact: contact, mvrPreview: null });
    };

    handleOpenVerifyMvr = (contact, rowsEmployerProfileId) => {
        const employerProfileId = this.getEmployerProfileId();
        if (!canVerifyMvr(contact, this.props.employerRegistered) || this._verifyingMvr ||
            this.state.loading || !employerProfileId || rowsEmployerProfileId !== employerProfileId ||
            this.state.rowsEmployerProfileId !== employerProfileId || !this.state.employees.includes(contact)) return;
        this.setState({
            modalOpen: true,
            selectedContact: contact,
            mvrPreview: { contact, employerProfileId, actorKey: this.getActorKey() }
        });
    };

    handleVerifyMvr = async (contact, rowsEmployerProfileId) => {
        const employerProfileId = this.getEmployerProfileId();
        const preview = this.state.mvrPreview;
        if (
            !this.state.modalOpen || !preview || preview.contact !== contact ||
            preview.employerProfileId !== employerProfileId || preview.actorKey !== this.getActorKey() ||
            !canVerifyMvr(contact, this.props.employerRegistered) ||
            this._verifyingMvr ||
            this.state.loading ||
            rowsEmployerProfileId !== employerProfileId ||
            this.state.rowsEmployerProfileId !== employerProfileId ||
            !this.state.employees.includes(contact)
        ) return;
        if (!employerProfileId) {
            this.props.templateStore.openSnackbar("Employer profile is required to verify MVR.");
            return;
        }
        this._verifyingMvr = true;
        this.setState({ verifyingContactId: contact.contactId });
        try {
            const result = await this.props.hgSuperPayStore.submitMvr(contact.contactId, employerProfileId);
            if (!this._mounted || this.state.mvrPreview !== preview ||
                preview.actorKey !== this.getActorKey() || employerProfileId !== this.getEmployerProfileId()) return;
            this.setState(({ employees }) => ({
                employees: employees.map(employee => String(employee.contactId) === String(contact.contactId)
                    ? { ...employee, mvrStatus: result.mvrStatus, mvrStatusReason: result.mvrStatusReason ?? null }
                    : employee)
            }));
            this.props.templateStore.openSnackbar(`MVR submitted. Fund verification: ${getMvrDetails(result.mvrStatus, true).label}.`);
            this.handleCloseModal();
            this._loadMapping(undefined, undefined, undefined, true);
        } catch (error) {
            if (!this._mounted || this.state.mvrPreview !== preview ||
                preview.actorKey !== this.getActorKey() || employerProfileId !== this.getEmployerProfileId()) return;
            this.props.templateStore.openSnackbar(parseApiError(error, "Failed to submit MVR verification."));
            this.handleCloseModal();
            this._loadMapping(undefined, undefined, undefined, true, true);
        } finally {
            this._verifyingMvr = false;
            if (this._mounted) this.setState({ verifyingContactId: null });
        }
    };

    handleCloseModal = () => {
        this.setState({
            modalOpen: false,
            selectedContact: null,
            mvrPreview: null,
            modalEmployee: null,
            registrationSuccess: false
        });
    };

    handleIntroductionVisibility = (visible, contextKey) => {
        this.setState({ mvrIntroductionVisible: visible, mvrIntroductionContext: contextKey });
    };

    handleOpenDeleteConfirm = (contact) => {
        this.setState({ deleteConfirmOpen: true, deleteContact: contact });
    };

    handleCloseDeleteConfirm = () => {
        this.setState({ deleteConfirmOpen: false, deleteContact: null });
    };

    handleConfirmDelete = () => {
        const { deleteContact, employees, page } = this.state;
        const { profile } = this.props.profileStore;
        const profileId = this.props.profileId || (profile && profile.id);
        this.setState({ deleteConfirmOpen: false, deleteContact: null });
        this.props.hgSuperPayStore.deleteEmployeeMapping(deleteContact.contactId, profileId)
            .then(() => this._loadMapping(employees.length === 1 && page > 1 ? page - 1 : page))
            .catch(() => {});
    };

    render() {
        const { currentProfileType, profile } = this.props.profileStore;
        const {
            employees, rowsEmployerProfileId, totalCount, loading, modalOpen, selectedContact,
            page, rowsPerPage, pagination, search, mappedFilter, verifyingContactId
        } = this.state;

        const { embedded, employerRegistered } = this.props;
        const announcement = resolveVisibleMvrAnnouncement(this.props.profileStore, this.props.organisationStore);
        const announcementKey = announcement ? String(announcement.profileId) : "";
        const introductionVisible = announcement && announcement.mvrAnnouncementRead === false &&
            (this.state.mvrIntroductionContext !== announcementKey || this.state.mvrIntroductionVisible !== false);

        const selectedName = selectedContact
            ? (selectedContact.profileName || `${selectedContact.firstName || ""} ${selectedContact.lastName || ""}`.trim() || "—")
            : "";

        const total = pagination ? pagination.total : totalCount;
        const currentPage = pagination ? pagination.page : page;
        const pageLimit = pagination ? pagination.limit : rowsPerPage;
        const totalPages = pagination ? pagination.totalPages : Math.max(1, Math.ceil(total / pageLimit));
        const hasPrevious = pagination ? pagination.hasPrevious : currentPage > 1;
        const hasNext = pagination ? pagination.hasNext : currentPage < totalPages;
        const displayedEmployees = employees;

        const mappingTable = (
            <Paper style={{ borderRadius: embedded ? 0 : 8, overflow: "hidden" }}>
                        <div style={{
                            backgroundColor: HOT_PINK,
                            padding: "12px 20px",
                            display: "flex",
                            alignItems: "center",
                            minHeight: 48
                        }}>
                            <Typography variant="subheading" style={{ color: "#fff", fontWeight: 700, letterSpacing: 0.5 }}>
                                Employee Mapping
                            </Typography>
                        </div>

                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 12,
                            padding: "14px 20px",
                            backgroundColor: "#fff",
                            borderBottom: "1px solid #eeeeee"
                        }}>
                            {/* Mapped / Not Mapped filter */}
                            <div style={{ display: "flex", gap: 6 }}>
                                {[
                                    { label: "All", value: null },
                                    { label: "Mapped", value: true },
                                    { label: "Not Mapped", value: false }
                                ].map(opt => {
                                    const active = mappedFilter === opt.value;
                                    return (
                                        <Button
                                            key={String(opt.value)}
                                            size="small"
                                            variant={active ? "contained" : "outlined"}
                                            onClick={() => !active && this.handleMappedFilterChange(opt.value)}
                                            disabled={loading}
                                            style={{
                                                borderRadius: 20,
                                                textTransform: "none",
                                                fontWeight: active ? 700 : 400,
                                                backgroundColor: active ? HOT_PINK : undefined,
                                                color: active ? "#fff" : undefined,
                                                borderColor: active ? HOT_PINK : "#ccc",
                                                minWidth: 90
                                            }}
                                        >
                                            {opt.label}
                                        </Button>
                                    );
                                })}
                            </div>

                            {/* Search */}
                            <TextField
                                value={search}
                                onChange={this.handleSearchChange}
                                label="Search employees"
                                placeholder="Name or email"
                                variant="outlined"
                                size="small"
                                margin="dense"
                                style={{ width: 300, maxWidth: "100%", margin: 0 }}
                                inputProps={{ "aria-label": "Search employee mappings" }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {search ? (
                                                <IconButton
                                                    aria-label="Clear employee search"
                                                    onClick={this.handleClearSearch}
                                                    size="small"
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            ) : (
                                                <SearchIcon style={{ color: "#9e9e9e" }} fontSize="small" />
                                            )}
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </div>

                        {!employerRegistered && (
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                padding: "12px 20px",
                                backgroundColor: "#fff8e1",
                                borderBottom: "1px solid #ffe082"
                            }}>
                                <WarningIcon style={{ color: "#e65100", flexShrink: 0, fontSize: 20 }} />
                                <Typography variant="body2" style={{ color: "#e65100" }}>
                                    Employee mapping will be available once your Employer registration is complete.
                                </Typography>
                            </div>
                        )}

                        {loading ? (
                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: 40 }}>
                                <CircularProgress style={{ color: HOT_PINK }} />
                            </div>
                        ) : (
                            <Table>
                                <TableHead>
                                    <TableRow style={{ backgroundColor: "#f5f5f5" }}>
                                        <TableCell style={{ fontWeight: 700, width: "30%" }}>HG Sole Trader Artists</TableCell>
                                        <TableCell style={{ fontWeight: 700, width: "25%" }}>Registered Email</TableCell>
                                        {this.props.showVenuesColumn && (
                                            <TableCell style={{ fontWeight: 700, width: "20%" }}>Venues Booked At</TableCell>
                                        )}
                                        <TableCell style={{ fontWeight: 700, width: "15%" }}>Mapping</TableCell>
                                        <TableCell style={{ fontWeight: 700, width: "15%" }}>Fund verification</TableCell>
                                        <TableCell style={{ fontWeight: 700, width: "20%" }}></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {displayedEmployees.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={this.props.showVenuesColumn ? 6 : 5} style={{ textAlign: "center", color: "#aaa", padding: 32 }}>
                                                No contacts found.
                                            </TableCell>
                                        </TableRow>
                                    ) : displayedEmployees.map(contact => {
                                        const mapped = !!contact.isMapped;
                                        const employeeAction = getEmployeeAction(contact);
                                        const displayName = contact.profileName || `${contact.firstName || ""} ${contact.lastName || ""}`.trim() || "—";
                                        const venueNames = Array.isArray(contact.venueNames)
                                            ? contact.venueNames
                                            : (contact.venues || []).map(v => v.name || v.profileName).filter(Boolean);
                                        return (
                                            <TableRow key={contact.contactId} hover>
                                                <TableCell>
                                                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                                                        {displayName}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" style={{ color: "#333" }}>
                                                        {contact.mygigstersEmail || <em style={{ color: "#aaa" }}>Not yet registered</em>}
                                                    </Typography>
                                                </TableCell>
                                                {this.props.showVenuesColumn && (
                                                    <TableCell>
                                                        <Typography variant="body2" style={{ color: "#555" }}>
                                                            {venueNames.length > 0
                                                                ? venueNames.join(", ")
                                                                : <em style={{ color: "#aaa" }}>—</em>
                                                            }
                                                        </Typography>
                                                    </TableCell>
                                                )}
                                                <TableCell>
                                                    {mapped ? (
                                                        <Chip
                                                            label="Mapped"
                                                            style={{ backgroundColor: "#e8f5e9", color: "#2e7d32", fontWeight: 700, fontSize: 12, height: 26 }}
                                                        />
                                                    ) : (
                                                        <Chip
                                                            label="Not Mapped"
                                                            style={{ backgroundColor: "#fce4ec", color: "#c62828", fontWeight: 700, fontSize: 12, height: 26 }}
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <MvrStatus status={contact.mvrStatus} reason={contact.mvrStatusReason} isMapped={mapped} />
                                                </TableCell>
                                                <TableCell>
                                                    {mapped ? (
                                                        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center" }}>
                                                            <Button
                                                                size="small"
                                                                style={{ color: HOT_PINK, fontWeight: 700, minWidth: 0, padding: "4px 8px" }}
                                                                onClick={() => this.handleOpenUpdateModal(contact)}
                                                            >
                                                                {employeeAction === "update" ? "UPDATE EMPLOYEE" : "VIEW"}
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                disabled={!canVerifyMvr(contact, employerRegistered) || verifyingContactId !== null || rowsEmployerProfileId !== this.getEmployerProfileId()}
                                                                style={{ color: HOT_PINK, fontWeight: 700, minWidth: 0, padding: "4px 8px" }}
                                                                onClick={() => this.handleOpenVerifyMvr(contact, rowsEmployerProfileId)}
                                                            >
                                                                {String(verifyingContactId) === String(contact.contactId) ? "VERIFYING..." : "VERIFY MVR"}
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                style={{ color: "#c62828", fontWeight: 700, minWidth: 0, padding: "4px 8px" }}
                                                                onClick={() => this.handleOpenDeleteConfirm(contact)}
                                                            >
                                                                DELETE
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <Button
                                                            size="small"
                                                            disabled={!employerRegistered}
                                                            style={{ color: employerRegistered ? HOT_PINK : undefined, fontWeight: 700, minWidth: 0, padding: "4px 8px" }}
                                                            onClick={() => this.handleOpenCreateModal(contact)}
                                                        >
                                                            SYNC EMPLOYEE
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                                <TableFooter>
                                    <TableRow>
                                        <TableCell colSpan={this.props.showVenuesColumn ? 6 : 5}>
                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", padding: "4px 0" }}>
                                                <Typography variant="caption" style={{ marginRight: 16, color: "#666" }}>
                                                    Rows per page: {pageLimit}
                                                </Typography>
                                                <Typography variant="caption" style={{ marginRight: 8, color: "#666" }}>
                                                    {total === 0 ? "0" : `${(currentPage - 1) * pageLimit + 1}-${Math.min(currentPage * pageLimit, total)}`} of {total}
                                                </Typography>
                                                <IconButton size="small" disabled={loading || !hasPrevious} onClick={() => this.handlePageChange(1)}>
                                                    <FirstPageIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" disabled={loading || !hasPrevious} onClick={() => this.handlePageChange(currentPage - 1)}>
                                                    <KeyboardArrowLeftIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" disabled={loading || !hasNext} onClick={() => this.handlePageChange(currentPage + 1)}>
                                                    <KeyboardArrowRightIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" disabled={loading || !hasNext} onClick={() => this.handlePageChange(totalPages)}>
                                                    <LastPageIcon fontSize="small" />
                                                </IconButton>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                </TableFooter>
                            </Table>
                        )}
            </Paper>
        );

        const dialogs = (
            <Fragment>

                <HGSuperPayEmployeeModal
                    open={modalOpen && (embedded || !introductionVisible)}
                    onClose={this.handleCloseModal}
                    contactId={selectedContact ? selectedContact.contactId : null}
                    employerProfileId={this.props.profileId || (profile ? profile.id : null)}
                    contactName={selectedName}
                    isMapped={!!(selectedContact && selectedContact.isMapped)}
                    employeeAction={selectedContact ? getEmployeeAction(selectedContact) : "view"}
                    mvrStatus={selectedContact && selectedContact.mvrStatus}
                    mvrStatusReason={selectedContact && selectedContact.mvrStatusReason}
                    verificationMode={!!this.state.mvrPreview}
                    previewContextKey={this.getActorKey()}
                    verifying={verifyingContactId !== null}
                    canVerify={canVerifyMvr(selectedContact, employerRegistered)}
                    onVerifyMvr={() => this.handleVerifyMvr(selectedContact, this.state.mvrPreview?.employerProfileId)}
                    onSuccess={this._loadMapping}
                />

                <Dialog open={this.state.deleteConfirmOpen} onClose={this.handleCloseDeleteConfirm} maxWidth="xs" fullWidth>
                    <DialogTitle>
                        <Typography variant="title" style={{ fontWeight: 700 }}>Remove Employee Mapping</Typography>
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1">
                            Are you sure you want to remove the HG Super Pay mapping for{" "}
                            <strong>
                                {this.state.deleteContact
                                    ? (this.state.deleteContact.profileName || `${this.state.deleteContact.firstName || ""} ${this.state.deleteContact.lastName || ""}`.trim())
                                    : "this employee"}
                            </strong>?
                            This will unlink them from HG Super Pay.
                        </Typography>
                    </DialogContent>
                    <DialogActions style={{ padding: "12px 20px" }}>
                        <Button onClick={this.handleCloseDeleteConfirm} style={{ color: "#888" }}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={this.handleConfirmDelete}
                            style={{ backgroundColor: "#c62828", color: "#fff", fontWeight: 700 }}
                        >
                            Remove
                        </Button>
                    </DialogActions>
                </Dialog>
            </Fragment>
        );

        if (embedded) {
            return (
                <Fragment>
                    {mappingTable}
                    {dialogs}
                </Fragment>
            );
        }

        if (process.env.REACT_APP_DISABLE_BILLING_MOBILE === "true") {
            return (
                <div>
                    <Header role={this.props.templateStore.getTypeString(currentProfileType || 2)} />
                    <Sidebar />
                    <MainContainer>
                        <Typography variant="display1" style={{ textAlign: "center", marginTop: 60, color: "#6b7280" }}>
                            This feature is only available on the desktop version.
                        </Typography>
                    </MainContainer>
                </div>
            );
        }

        return (
            <div>
                <Header role={this.props.templateStore.getTypeString(currentProfileType || 2)} />
                <Sidebar />
                <MainContainer>
                    <MvrIntroductionDialog onVisibilityChange={this.handleIntroductionVisibility} />
                    <Heading title="HG Super Pay Employees" />
                    {mappingTable}
                </MainContainer>
                {dialogs}
            </div>
        );
    }
}

export default HGSuperPayEmployees;
